import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { execute } from '@/graphql/execute';
import { UserOrganizationRole } from '@/graphql/graphql';
import { inviteUsersToOrganizationMutation } from '@/queries/organization.query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface InviteRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserOrganizationRole;
}

function createEmptyRow(): InviteRow {
  return { id: crypto.randomUUID(), firstName: '', lastName: '', email: '', role: 'MEMBER' };
}

interface UserInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

export function UserInviteDialog({ open, onOpenChange, organizationId, onSuccess }: UserInviteDialogProps) {
  const [rows, setRows] = useState<InviteRow[]>([createEmptyRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('users');

  const queryClient = useQueryClient();

  const inviteMutation = useMutation({
    mutationFn: async (invitees: InviteRow[]) => {
      return execute(inviteUsersToOrganizationMutation, {
        organizationId,
        invitees: invitees.map((r) => ({
          email: r.email,
          firstname: r.firstName,
          lastname: r.lastName || null,
          role: r.role,
        })),
      });
    },
    onSuccess: (_, variables) => {
      analytics.capture('Users Invited', { count: variables.length });
      toast.success(t('invite.inviteSuccess', { count: variables.length }));
      queryClient.invalidateQueries({ queryKey: ['users', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['invitations', organizationId] });
      onSuccess?.();
      onOpenChange(false);
      setRows([createEmptyRow()]);
    },
    onError: (error: any) => {
      if (typeof error?.message === 'string' && error.message.includes('DISPOSABLE_EMAIL_DOMAIN')) {
        toast.error(t('invite.disposableEmail'));
        return;
      }
      toast.error(error.message || t('invite.failedInvite'));
    },
  });

  const updateRow = (id: string, field: keyof InviteRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validRows = rows.filter((r) => r.email.trim());
    if (validRows.length === 0) {
      toast.error(t('invite.emailRequired_validation'));
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteMutation.mutateAsync(validRows);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSubmitting) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setRows([createEmptyRow()]);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[750px]'>
        <DialogHeader>
          <DialogTitle>{t('invite.title')}</DialogTitle>
          <DialogDescription>{t('invite.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('invite.firstName')}</TableHead>
                  <TableHead>{t('invite.lastName')}</TableHead>
                  <TableHead>{t('invite.emailRequired')}</TableHead>
                  <TableHead>{t('invite.role')}</TableHead>
                  <TableHead className='w-[40px]' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className='p-1.5'>
                      <Input
                        value={row.firstName}
                        onChange={(e) => updateRow(row.id, 'firstName', e.target.value)}
                        placeholder={t('invite.firstNamePlaceholder')}
                        className='h-8'
                      />
                    </TableCell>
                    <TableCell className='p-1.5'>
                      <Input
                        value={row.lastName}
                        onChange={(e) => updateRow(row.id, 'lastName', e.target.value)}
                        placeholder={t('invite.lastNamePlaceholder')}
                        className='h-8'
                      />
                    </TableCell>
                    <TableCell className='p-1.5'>
                      <Input
                        type='email'
                        value={row.email}
                        onChange={(e) => updateRow(row.id, 'email', e.target.value)}
                        placeholder={t('invite.emailPlaceholder')}
                        className='h-8'
                        required
                      />
                    </TableCell>
                    <TableCell className='p-1.5'>
                      <Select
                        value={row.role}
                        onValueChange={(value: UserOrganizationRole) => updateRow(row.id, 'role', value)}
                      >
                        <SelectTrigger className='h-8'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='MEMBER'>{t('invite.roleMember')}</SelectItem>
                          <SelectItem value='EDITOR'>{t('invite.roleEditor')}</SelectItem>
                          <SelectItem value='ADMIN'>{t('invite.roleAdmin')}</SelectItem>
                          <SelectItem value='BILLING_MANAGER'>{t('invite.roleBillingManager')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className='p-1.5'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8'
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                      >
                        <Trash2 className='size-4 text-muted-foreground' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button type='button' variant='ghost' size='sm' className='mt-2' onClick={addRow}>
            <Plus className='size-4 mr-1' />
            {t('invite.addAnother')}
          </Button>
          <DialogFooter className='mt-4'>
            <Button type='button' variant='outline' onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              {t('invite.cancel')}
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting
                ? t('invite.inviting')
                : t('invite.inviteCount', { count: rows.filter((r) => r.email.trim()).length || 0 })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
